import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { isHubRouteAdminBypass } from '../constants/roles';

/**
 * Permission that grants the metrics dashboard (PBI #3968). Seeded to the
 * admin profiles by `migrations/auto/2026_08_06_01_add_dashboard_read_permission`
 * and carried forward by SUPERUSER_PERMISSIONS / ADMIN_PERMISSIONS.
 */
export const DASHBOARD_READ_PERMISSION = 'dashboard:read';

/**
 * Whether this session may see the metrics dashboard.
 *
 * Same shape every other gate in the app uses (`ProtectedRoute`, the sidebar,
 * the quick-actions catalog): the paired permission, with admin bypassing it.
 * The bypass is deliberate — an environment where the permission has not been
 * granted yet keeps its admins on the dashboard instead of emptying it.
 *
 * Returning false is not an error state: the user lands on the Painel
 * (`DashboardCommonHome`), which is that profile's home page.
 */
export function useCanViewDashboard() {
  const { user } = useAuth();

  return useMemo(() => {
    const isAdmin = Boolean(user?.is_superuser)
      || Boolean(user?.is_platform_admin)
      || isHubRouteAdminBypass([
        ...(user?.roles || []),
        ...(user?.platform_roles || []),
      ]);

    // Read straight off the session, like AppShell's own `hasPermission`, so
    // the hook stays usable wherever `user` is available.
    return isAdmin || (user?.permissions || []).includes(DASHBOARD_READ_PERMISSION);
  }, [user]);
}

export default useCanViewDashboard;
